import { FC, useCallback, useMemo } from "react";
import styled from "styled-components";
import TaskListLayout from "../layouts/TaskListLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { LayoutSidebarInset } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { reserveForEmail } from "../../redux/apis/blenderApiSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectAuth } from "../../redux/slices/authSlice";
import {
  toggleTaskListCollapsed,
  setSelectedWorkItemId,
  toggleTaskManagerOpen,
} from "../../redux/slices/consolePageSlice";
import HideShowButton from "../presenters/_shared/HideShowButton";
import TaskListItem from "../presenters/task-list/TaskListItem";

interface IProps {}

const RootContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${(props) => props.theme.primary};
  filter: drop-shadow(0 10px 8px rgb(0 0 0 / 0.04))
    drop-shadow(0 4px 3px rgb(0 0 0 / 0.1));
`;

const TopBarContainer = styled.div<{ isTaskListCollapsed: boolean }>`
  height: 100%;
  display: flex;
  padding-left: ${(props) => (props.isTaskListCollapsed ? "0.5rem" : "1rem")};
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.primary};
  border-bottom: 1px solid ${(props) => props.theme.default};
  transition: all 0.3s ease-out;
  cursor: pointer;
`;

const TopBarContentContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  overflow: hidden;
  overflow-wrap: nowrap;
  word-break: keep-all;
  align-items: center;
  justify-content: center;
`;

const TopBarTitleContainer = styled.div`
  padding-left: 0.5rem;
`;

const ToggleButtonContainer = styled.div`
  width: 1.5rem;
  height: 2rem;
`;

const BottomBarContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OutboundEmailButton = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: ${(props) => props.theme.selected};
  background-color: ${(props) => props.theme.neutral};
  filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.1))
    drop-shadow(0 1px 1px rgb(0 0 0 / 0.06));
  :hover {
    background-color: ${(props) => props.theme.unselectedIcon};
  }
`;

const TaskListContainer: FC<IProps> = () => {
  const dispatch = useAppDispatch();

  const {
    isTaskListCollapsed,
    selectedWorkItemId,
    customerNames,
    unreadMessageCounts,
    isTaskManagerOpen
  } = useAppSelector((state) => state.consolePageState);
  const { agentProperties, workItems } = useAppSelector(
    (state) => state.blenderState.agentClientState
  );
  const { token } = useSelector(selectAuth);

  const toggleTaskManager = useCallback(() => {
    dispatch(toggleTaskListCollapsed(!isTaskListCollapsed));
  }, [dispatch, isTaskListCollapsed]);

  const workItemClickHandler = useCallback(
    (workItemId: string) => {
      dispatch(setSelectedWorkItemId(workItemId));
    },
    [dispatch]
  );

  const openTaskManagerClickHandler = useCallback(() => {
    dispatch(toggleTaskManagerOpen());
  }, [dispatch]);

  const createEmailPreview = useCallback(() => {
    dispatch(reserveForEmail(token));
  }, [dispatch, token]);

  const items = useMemo(() => {
    if (!workItems) return <></>;
    const workItemIds = Object.keys(workItems);
    if (
      workItemIds.length > 0 &&
      (selectedWorkItemId === "" ||
        workItemIds.indexOf(selectedWorkItemId) === -1)
    ) {
      // If the selected work item is not in the list, select the first one, TODO: move this to page
      workItemClickHandler(workItemIds[0]);
    }
    return Object.values(workItems).map((workItem) => (
      <TaskListItem
        workItem={workItem}
        customerName={customerNames[workItem.customerId]}
        selectedWorkItemId={selectedWorkItemId}
        isTaskListCollapsed={isTaskListCollapsed}
        workItemClicked={workItemClickHandler}
        openTaskManagerClicked={openTaskManagerClickHandler}
        unreadMessageCount={unreadMessageCounts[workItem.primaryConversationId]}
        key={workItem.workItemId}
        isTaskManagerOpen={isTaskManagerOpen}
      />
    ));
  }, [
    customerNames,
    isTaskListCollapsed,
    openTaskManagerClickHandler,
    selectedWorkItemId,
    unreadMessageCounts,
    workItemClickHandler,
    workItems,
    isTaskManagerOpen
  ]);

  return (
    <RootContainer>
      <TaskListLayout>
        {{
          top: (
            <TopBarContainer isTaskListCollapsed={isTaskListCollapsed}>
              <TopBarContentContainer onClick={openTaskManagerClickHandler}>
                <LayoutSidebarInset fontSize={"1.5rem"} />
                {!isTaskListCollapsed && (
                  <TopBarTitleContainer>Task Manager</TopBarTitleContainer>
                )}
              </TopBarContentContainer>
              <ToggleButtonContainer>
                <HideShowButton
                  id="task_list_toggle_btn"
                  eventHandler={toggleTaskManager}
                  direction={isTaskListCollapsed ? "right" : "left"}
                />
              </ToggleButtonContainer>
            </TopBarContainer>
          ),
          content: <div>{items}</div>,
          bottom: (
            <BottomBarContainer>
              {agentProperties.capabilities.includes("Email") && (
                <OutboundEmailButton id="outbound_email_btn" onClick={createEmailPreview}>
                  <FontAwesomeIcon icon={faEnvelope} />
                </OutboundEmailButton>
              )}
            </BottomBarContainer>
          ),
        }}
      </TaskListLayout>
    </RootContainer>
  );
};

export default TaskListContainer;
